import { Button, Flex, Table, TableRowMenuCell, Text } from '@/components';
import { useProjectPermission } from '@/features/authentication/_hooks/permission';
import { sortBelanjaProgramData } from '@/features/mold/utils';
import { useProject } from '@/features/project/_hooks/project';
import { useId } from '@/hooks';
import {
  TABLE_ROW_MENU_WIDTH,
  formatFraction,
  formatValueByUnit,
} from '@/utils';
import Column from 'antd/es/table/Column';
import ColumnGroup from 'antd/es/table/ColumnGroup';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { IndonesiaData } from '../../../_models/indonesia-data';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';

type Props = {
  data: IndonesiaData['belanjaProgram'];
  fiscalYear: number;
  onClickDelete: (value: { id: string; type: 'Biaya' | 'Belanja' }) => void;
};

export const BelanjaProgramTable: FC<Props> = ({
  data,
  fiscalYear,

  onClickDelete,
}) => {
  const { t } = useTranslation();
  const { id } = useId();
  const { setDrawerState } = useProjectDetailDrawerState();
  const project = useProject(id);
  const editPermission = useProjectPermission(true);

  const enableEdit =
    project.data && !project.data.isLocked && editPermission.data;

  return (
    <Table
      bordered={true}
      enableSticky={false}
      rowKey="id"
      dataSource={sortBelanjaProgramData(data)}
      locale={{ emptyText: 'Tidak ada data.' }}
      pagination={false}
    >
      <Column
        title={t('uraian-belanja-program')}
        dataIndex="name"
        key="name"
        render={(_, { name, childIndex, parentId }) => {
          return (
            <Text
              weight="regular"
              text={`${parentId ? `- ${childIndex}.` : ''}${name}`}
            />
          );
        }}
      />
      <ColumnGroup title={fiscalYear}>
        <Column
          title={t('finalized-amount')}
          dataIndex="currentYearAnggaranAPBD"
          render={(value) => formatValueByUnit(value)}
        />
        <Column
          title={t('next-request-amount')}
          dataIndex="currentYearAnggaranLainnya"
          render={(value) => formatValueByUnit(value)}
        />
        <Column
          title={t('execution-amount')}
          dataIndex="currentYearRealisasi"
          render={(value) => formatValueByUnit(value)}
        />
        <Column
          title={t('percentage-unit')}
          dataIndex="currentYearRate"
          render={(value) => formatFraction(value, 100)}
        />
      </ColumnGroup>
      <ColumnGroup title={fiscalYear - 1}>
        <Column
          title={t('finalized-amount')}
          dataIndex="previousYearAnggaranAPBD"
          render={(value) => formatValueByUnit(value)}
        />
        <Column
          title={t('next-request-amount')}
          dataIndex="previousYearAnggaranLainnya"
          render={(value) => formatValueByUnit(value)}
        />
        <Column
          title={t('execution-amount')}
          dataIndex="previousYearRealisasi"
          render={(value) => formatValueByUnit(value)}
        />
        <Column
          title={t('percentage-unit')}
          dataIndex="previousYearRate"
          render={(value) => formatFraction(value, 100)}
        />
      </ColumnGroup>
      {enableEdit && (
        <Column
          title={
            <Flex justify="center">
              <Button
                onClick={() => {
                  setDrawerState({
                    open: true,
                    type: 'expenditure-item',
                    isEditing: false,
                    mode: 'Belanja',
                  });
                }}
              >
                {t('create')}
              </Button>
            </Flex>
          }
          width={TABLE_ROW_MENU_WIDTH}
          dataIndex="actions"
          render={(_, record) => {
            return (
              <Flex justify="center">
                <TableRowMenuCell
                  onEdit={() => {
                    setDrawerState({
                      open: true,
                      type: 'expenditure-item',
                      isEditing: true,
                      id: record.id,
                      mode: 'Belanja',
                    });
                  }}
                  items={[
                    {
                      key: 'delete',
                      label: t('delete'),
                      danger: true,
                      onClick: () => {
                        onClickDelete({
                          id: record.id,
                          type: 'Belanja',
                        });
                      },
                    },
                  ]}
                />
              </Flex>
            );
          }}
        />
      )}
    </Table>
  );
};
